import {TaskLeadingVisual} from '@github-ui/agent-sessions/components/TaskLeadingVisual'
import type {HydratedAPITask} from '@github-ui/agent-sessions/types/api-task'
import type {SessionState} from '@github-ui/agent-sessions/types/session'
import {AgentIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {clsx} from 'clsx'
import type {FC} from 'react'
import styles from './Finding.module.css'

export interface FixTaskInfo {
  ccaTaskId: string
  state: SessionState
}

export const fixTaskStatusMap: Record<string, {label: string; color: string}> = {
  in_progress: {label: 'Fixing', color: 'color-fg-attention'},
  queued: {label: 'Fixing', color: 'color-fg-attention'},
  completed: {label: 'Fixed', color: 'color-fg-success'},
  failed: {label: 'Fix failed', color: 'color-fg-danger'},
  timed_out: {label: 'Fix failed', color: 'color-fg-danger'},
  cancelled: {label: 'Fix cancelled', color: 'color-fg-danger'},
}

export const FixFindingButton: FC<{
  resolved: boolean
  fixTask?: FixTaskInfo
  onFix: () => void
}> = ({resolved, fixTask, onFix}) => {
  if (resolved) return null

  if (!fixTask) {
    return (
      <Button leadingVisual={AgentIcon} onClick={onFix}>
        Fix with Copilot
      </Button>
    )
  }

  const canRetry = fixTask.state === 'failed' || fixTask.state === 'timed_out' || fixTask.state === 'cancelled'
  if (!canRetry) return null

  return (
    <Button leadingVisual={AgentIcon} onClick={onFix}>
      Retry fix
    </Button>
  )
}

export const FixTaskHeaderStatus: FC<{fixTask: FixTaskInfo; taskUrl?: string}> = ({fixTask, taskUrl}) => {
  const status = fixTaskStatusMap[fixTask.state]
  if (!status) return null

  const StatusLabel = taskUrl ? 'a' : 'span'

  return (
    <span className={`d-flex flex-items-center gap-1 ${status.color}`}>
      <TaskLeadingVisual sessionState={fixTask.state} />
      <StatusLabel href={taskUrl} className={clsx('text-small', status.color, taskUrl && 'Link--inTextBlock')}>
        {status.label}
      </StatusLabel>
    </span>
  )
}

export const FixTaskExpandedStatus: FC<{fixTask: FixTaskInfo; taskData?: HydratedAPITask; taskUrl?: string}> = ({
  fixTask,
  taskData,
  taskUrl,
}) => {
  const status = fixTaskStatusMap[fixTask.state]
  if (!status) return null

  const taskTitle = taskData?.name ?? 'New task'
  const latestSession = taskData?.sessions?.[taskData.sessions.length - 1]
  const model = latestSession?.model
  const sessionCount = taskData?.session_count ?? 0
  const premiumRequests = taskData?.sessions?.reduce((sum, s) => sum + (s.premium_requests ?? 0), 0) ?? 0
  const TaskStatusCardWrapper = taskUrl ? 'a' : 'div'

  return (
    <div className={styles.taskStatusSection}>
      <h3 className={styles.sectionTitle}>Agent session</h3>
      <TaskStatusCardWrapper
        href={taskUrl}
        className={styles.taskStatusCard}
        aria-label={taskUrl ? `View agent session ${taskTitle}: ${status.label}` : undefined}
      >
        <TaskLeadingVisual sessionState={fixTask.state} />
        <div className={styles.taskStatusContent}>
          <span className="text-semibold color-fg-default">{taskTitle}</span>
          <span className="d-flex flex-items-center flex-wrap gap-1 color-fg-muted text-small">
            <span className={clsx(styles.metaItem, status.color)}>{status.label}</span>
            {model && <span className={styles.metaItem}>{model}</span>}
            {sessionCount > 0 && (
              <span
                className={styles.metaItem}
              >{`${sessionCount} ${sessionCount === 1 ? 'session' : 'sessions'}`}</span>
            )}
            {premiumRequests > 0 && (
              <span
                className={styles.metaItem}
              >{`${premiumRequests} ${premiumRequests === 1 ? 'premium request' : 'premium requests'}`}</span>
            )}
          </span>
        </div>
      </TaskStatusCardWrapper>
    </div>
  )
}
